import React from 'react';

export interface RichTextProps extends React.HTMLAttributes<HTMLElement> {
  text: string;
  className?: string;
  as?: 'p' | 'span' | 'div';
}

/**
 * Parses markdown-like syntax in JSON translation strings.
 * Supports [link text](url) for external AND internal links, and **bold** for emphasis.
 * External links (https://) open in new tab; internal links (/) stay in same tab.
 */
export function RichText({ text, className, as: Component = 'p', ...rest }: RichTextProps) {
  const parts = parseRichText(text);
  return <Component className={className} {...rest}>{parts}</Component>;
}

function parseRichText(text: string): React.ReactNode[] {
  const regex = /\[([^\]]+)\]\(((?:https?:\/\/|\/)[^)]+)\)|\*\*([^*]+)\*\*/g;
  const tokens: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      tokens.push(text.slice(lastIndex, match.index));
    }

    if (match[1] && match[2]) {
      const isFullUrl = match[2].startsWith('http');
      const isOwnDomain = isFullUrl && match[2].includes('jamezz.com');
      const isExternal = isFullUrl && !isOwnDomain;
      tokens.push(
        <a
          key={match.index}
          href={match[2]}
          {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer nofollow' } : {})}
        >
          {match[1]}
        </a>
      );
    } else if (match[3]) {
      tokens.push(<strong key={match.index}>{match[3]}</strong>);
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    tokens.push(text.slice(lastIndex));
  }

  return tokens;
}
